import React, { useState } from 'react';
import { Box, Typography, Paper, Button, Collapse } from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { generateTemplatePreviewEnhanced } from '../../utils/executionUtils';

/**
 * TemplatePreviewSection Component
 *
 * Section for displaying template preview with current values.
 *
 * @param {Object} props
 * @param {Object} props.template - Template object
 * @param {Object} props.executionData - Execution data object
 */
function TemplatePreviewSection({ template, executionData }) {
  const [previewExpanded, setPreviewExpanded] = useState(false);

  const getTemplatePreview = () => {
    if (
      !template?.template ||
      !executionData.input_data ||
      typeof executionData.input_data !== 'object' ||
      Object.keys(executionData.input_data).length === 0
    ) {
      return null;
    }

    const previewText = generateTemplatePreviewEnhanced(
      template,
      executionData.input_data
    );
    const PREVIEW_MIN_LENGTH = 200;
    const isLongText = previewText.length > PREVIEW_MIN_LENGTH;

    return { previewText, isLongText };
  };

  const templatePreview = getTemplatePreview();

  if (!templatePreview) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        Template Preview
      </Typography>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          bgcolor: 'grey.50',
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          whiteSpace: 'pre-wrap',
          minHeight: '80px',
          position: 'relative',
        }}
      >
        <Collapse in={previewExpanded} collapsedSize={100}>
          <Typography
            component="pre"
            sx={{
              margin: 0,
              fontSize: '0.875rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {templatePreview.previewText}
          </Typography>
        </Collapse>
        {templatePreview.isLongText && (
          <Button
            size="small"
            onClick={() => setPreviewExpanded(!previewExpanded)}
            endIcon={previewExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              mt: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              color: 'primary.main',
              minWidth: 'auto',
              px: 1,
            }}
          >
            {previewExpanded ? 'Show less' : 'Show more'}
          </Button>
        )}
      </Paper>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, display: 'block' }}
      >
        💡 This shows how your template will look with the current values. Empty
        variables will show as {'{{variable}}'}.
      </Typography>
    </Box>
  );
}

export default TemplatePreviewSection;
